import type * as Api from '@ViewModels';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateAutomationStepMutation } from '../../../../queries/Automation';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';

/**
 * Current status of the auto-save system.
 *
 * Status flow:
 * - idle: No pending saves, no recent activity
 * - saving: Currently sending save request(s) to the server
 * - saved: Successfully saved, briefly shown before returning to idle
 * - error: Save failed after all retries exhausted
 * - retrying: Save failed, attempting retry with exponential backoff
 */
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'retrying';

/**
 * Configuration options for the useAutoSave hook.
 */
interface UseAutoSaveOptions {
	/** Automation template ID that steps belong to */
	templateId: string | undefined;
	/** Whether auto-save is enabled (default: true) */
	enabled?: boolean;
	/** Milliseconds to wait after last change before saving (default: 500) */
	debounceMs?: number;
	/** Maximum number of retry attempts on failure (default: 3) */
	maxRetries?: number;
	/** Base delay in milliseconds between retries, grows exponentially (default: 1000) */
	retryDelayMs?: number;
	/** Callback invoked after successful save */
	onSaveSuccess?: () => void;
	/** Callback invoked when save fails (includes retry attempts) */
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Return value from useAutoSave providing save state and controls.
 */
interface UseAutoSaveReturn {
	/** Current status of the save operation */
	saveStatus: SaveStatus;
	/** Timestamp of the last successful save, null if never saved */
	lastSavedAt: Date | null;
	/** Whether there are unsaved changes pending in the queue */
	isDirty: boolean;
	/** Current retry attempt number (0 if not retrying) */
	retryCount: number;
	/** Function to queue a step for saving with debouncing */
	saveStep: (stepId: string, stepType: Api.AutomationStepType, step: Api.IAutomationStep) => Promise<void>;
	/** Function to clear the save queue and mark as clean */
	markClean: () => void;
	/** Error message from the last failed save, null if no error */
	errorMessage: string | null;
}

/**
 * Provides automatic saving of automation steps with debouncing and retry logic.
 *
 * This hook implements a queue-based auto-save system that batches multiple step
 * updates and handles network failures gracefully. It's designed to provide a
 * seamless editing experience where changes are automatically persisted without
 * requiring manual save actions.
 *
 * Key behaviors:
 * - Debounces save requests to avoid overwhelming the server with rapid changes
 * - Queues multiple step updates and sends them in parallel batches
 * - Retries failed saves with exponential backoff (1s, 2s, 4s by default)
 * - Tracks which steps succeeded vs failed in a batch, only retries failures
 * - Prevents memory leaks by tracking component mount state
 * - Clears timers on unmount to avoid stale saves
 *
 * Save flow:
 * 1. User changes a step → saveStep() is called
 * 2. Step is added to queue (or replaces existing entry for same stepId)
 * 3. Debounce timer starts (or resets if already running)
 * 4. After debounce period, processSaveQueue() sends all queued steps
 * 5. Successfully saved steps are removed from queue
 * 6. Failed steps remain in queue and retry with exponential backoff
 * 7. After maxRetries, status changes to 'error' and queue is cleared
 *
 * Why queue-based?
 * - Prevents race conditions when user rapidly edits multiple steps
 * - Batches API calls for better performance
 * - Handles partial failures (some steps save, others don't)
 * - Latest change to a step always wins (map structure by stepId)
 *
 * @param options - Configuration object
 * @param options.templateId - Automation template ID
 * @param options.enabled - Enable auto-save (default: true)
 * @param options.debounceMs - Debounce delay in ms (default: 500)
 * @param options.maxRetries - Max retry attempts (default: 3)
 * @param options.retryDelayMs - Base retry delay in ms (default: 1000)
 * @param options.onSaveSuccess - Callback on successful save
 * @param options.onSaveError - Callback on save error
 * @returns Object with save state and control functions
 *
 * @example
 * ```typescript
 * const { saveStep, saveStatus, isDirty, lastSavedAt } = useAutoSave({
 *   templateId: automation.id,
 *   enabled: !isReadOnly,
 *   debounceMs: 500,
 *   onSaveSuccess: () => {
 *     showToast('Saved successfully');
 *   },
 *   onSaveError: (error) => {
 *     showErrorToast(error.systemMessage);
 *   }
 * });
 *
 * // In step editor:
 * const handleStepChange = (updatedStep: Api.IAutomationStep) => {
 *   saveStep(step.id, step.stepType, updatedStep);
 * };
 *
 * // Show save indicator:
 * {saveStatus === 'saving' && <Spinner />}
 * {saveStatus === 'saved' && <CheckIcon />}
 * {isDirty && <UnsavedIndicator />}
 * ```
 */
export function useAutoSave({
	templateId,
	enabled = true,
	debounceMs = 500,
	maxRetries = 3,
	retryDelayMs = 1000,
	onSaveSuccess,
	onSaveError,
	impersonationContext,
}: UseAutoSaveOptions): UseAutoSaveReturn {
	const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const [isDirty, setIsDirty] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [retryCount, setRetryCount] = useState(0);
	const { automationTemplate: rootAutomationTemplate } = useAutomationFlowContext();
	const relatedTemplateIds = useMemo(() => {
		if (!rootAutomationTemplate?.id || !templateId || rootAutomationTemplate.id === templateId) {
			return undefined;
		}
		return [rootAutomationTemplate.id];
	}, [rootAutomationTemplate?.id, templateId]);

	const saveQueueRef = useRef<Map<string, { stepType: Api.AutomationStepType; step: Api.IAutomationStep }>>(new Map());
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
	const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
	const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	// Track mount state to prevent setting state after unmount
	const mountedRef = useRef(true);

	const updateStepMutation = useUpdateAutomationStepMutation({
		onSuccess: () => {
			if (mountedRef.current) {
				setLastSavedAt(new Date());
			}
			onSaveSuccess?.();
		},
		onError: error => {
			if (mountedRef.current) {
				setSaveStatus('error');
				setErrorMessage(error.systemMessage || 'Failed to save step');
			}
			onSaveError?.(error);
		},
	});

	const processSaveQueue = useCallback(async () => {
		if (!templateId || !enabled || saveQueueRef.current.size === 0) {
			return;
		}

		if (!mountedRef.current) {
			return;
		}

		setSaveStatus(retryCount > 0 ? 'retrying' : 'saving');
		setErrorMessage(null);

		try {
			// Use Promise.allSettled to handle partial failures properly
			const savePromises = Array.from(saveQueueRef.current.entries()).map(([stepId, { stepType, step }]) => {
				return updateStepMutation
					.mutateAsync({
						templateId,
						stepType,
						stepId,
						step,
						impersonationContext,
						relatedTemplateIds,
					})
					.then(() => ({ status: 'fulfilled' as const, stepId }))
					.catch((error: unknown) => ({ status: 'rejected' as const, stepId, error }));
			});

			const results = await Promise.all(savePromises);

			// Remove only successfully saved steps from queue
			const successfulStepIds = results
				.filter(result => result.status === 'fulfilled')
				.map(result => (result as { status: 'fulfilled'; stepId: string }).stepId);

			for (const stepId of successfulStepIds) {
				saveQueueRef.current.delete(stepId);
			}

			const hasFailures = results.some(result => result.status === 'rejected');

			if (!mountedRef.current) {
				return;
			}

			if (hasFailures) {
				// Some saves failed, will retry remaining items
				if (retryCount < maxRetries) {
					const nextRetryCount = retryCount + 1;
					setRetryCount(nextRetryCount);
					setSaveStatus('retrying');
					const delay = retryDelayMs * Math.pow(2, nextRetryCount - 1);

					if (retryTimerRef.current) {
						clearTimeout(retryTimerRef.current);
					}

					retryTimerRef.current = setTimeout(() => {
						if (mountedRef.current) {
							processSaveQueue();
						}
					}, delay);
				} else {
					setSaveStatus('error');
					setRetryCount(0);
				}
			} else {
				// All saves succeeded
				setRetryCount(0);
				setIsDirty(false);
				setSaveStatus('saved');

				if (savedTimeoutRef.current) {
					clearTimeout(savedTimeoutRef.current);
				}

				savedTimeoutRef.current = setTimeout(() => {
					if (mountedRef.current) {
						setSaveStatus('idle');
					}
				}, 2000);
			}
		} catch {
			// This catch should rarely be hit now since we handle rejections above
			if (!mountedRef.current) {
				return;
			}

			if (retryCount < maxRetries) {
				const nextRetryCount = retryCount + 1;
				setRetryCount(nextRetryCount);
				setSaveStatus('retrying');
				const delay = retryDelayMs * Math.pow(2, nextRetryCount - 1);

				if (retryTimerRef.current) {
					clearTimeout(retryTimerRef.current);
				}

				retryTimerRef.current = setTimeout(() => {
					if (mountedRef.current) {
						processSaveQueue();
					}
				}, delay);
			} else {
				setSaveStatus('error');
				setRetryCount(0);
			}
		}
	}, [
		templateId,
		enabled,
		updateStepMutation,
		retryCount,
		maxRetries,
		retryDelayMs,
		impersonationContext,
		relatedTemplateIds,
	]);

	const saveStep = useCallback(
		async (stepId: string, stepType: Api.AutomationStepType, step: Api.IAutomationStep) => {
			if (!templateId || !enabled || !mountedRef.current) {
				return;
			}

			saveQueueRef.current.set(stepId, { stepType, step });
			setIsDirty(true);

			// Clear existing timer before creating new one to prevent memory leaks
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			debounceTimerRef.current = setTimeout(() => {
				if (mountedRef.current) {
					processSaveQueue();
				}
			}, debounceMs);
		},
		[templateId, enabled, debounceMs, processSaveQueue]
	);

	const markClean = useCallback(() => {
		setIsDirty(false);
		setRetryCount(0);
		saveQueueRef.current.clear();
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		if (retryTimerRef.current) {
			clearTimeout(retryTimerRef.current);
		}
	}, []);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
			}
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	return {
		saveStatus,
		lastSavedAt,
		isDirty,
		retryCount,
		saveStep,
		markClean,
		errorMessage,
	};
}
